import { useEffect, useState } from "react";
import { listSuppliers } from "../erpClient";
import { isValidMobileNumber } from "./mobileInput";

export const SUPPLIER_CONTACT_EXISTS_MESSAGE =
  "Another supplier with this contact number already exists";

function extractSuppliersArray(res) {
  if (!res) return [];
  const d = res.data;
  if (Array.isArray(d?.suppliers)) return d.suppliers;
  if (Array.isArray(d)) return d;
  return [];
}

function getContactNumber(row) {
  return String(row?.contact_number ?? row?.contactNumber ?? "").trim();
}

function supplierMatchesContact(row, contact) {
  return getContactNumber(row) === contact;
}

function isExcludedSupplier(row, excludeSupplierId) {
  if (excludeSupplierId == null || excludeSupplierId === "") return false;
  return String(row?.id) === String(excludeSupplierId);
}

export function useSupplierContactExistsCheck(
  contactNumber,
  {
    enabled = true,
    excludeSupplierId = null,
    localSuppliers = [],
  } = {},
) {
  const [exists, setExists] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setExists(false);
      setChecking(false);
      return;
    }

    const trimmed = String(contactNumber ?? "").trim();
    if (!isValidMobileNumber(trimmed)) {
      setExists(false);
      setChecking(false);
      return;
    }

    const localMatch = (Array.isArray(localSuppliers) ? localSuppliers : []).some(
      (row) =>
        supplierMatchesContact(row, trimmed) &&
        !isExcludedSupplier(row, excludeSupplierId),
    );

    if (localMatch) {
      setExists(true);
      setChecking(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const response = await listSuppliers({ search: trimmed });
        if (cancelled) return;

        const rows = extractSuppliersArray(response);
        const remoteMatch = rows.some(
          (row) =>
            supplierMatchesContact(row, trimmed) &&
            !isExcludedSupplier(row, excludeSupplierId),
        );

        setExists(remoteMatch);
      } catch {
        if (!cancelled) setExists(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [contactNumber, enabled, excludeSupplierId, localSuppliers]);

  return {
    exists,
    checking,
    message: exists ? SUPPLIER_CONTACT_EXISTS_MESSAGE : "",
  };
}
